import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Clock, CheckCircle, Download } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { ResumeUpload } from '../components/resume/ResumeUpload';
import { ResumeComments } from '../components/resume/ResumeComments';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { toast } from 'react-hot-toast';

interface ResumeReview {
  id: string;
  resume_url: string;
  status: 'pending' | 'reviewed';
  admin_feedback?: string;
  created_at: string;
}

function ResumeReviewPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [email, setEmail] = useState('');
  const [reviews, setReviews] = useState<ResumeReview[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState<string | null>(null);
  const [showReviewContainer, setShowReviewContainer] = useState(false);
  const [currentReview, setCurrentReview] = useState<ResumeReview | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      loadReviews();
    }
  }, [user]);

  const loadReviews = async () => {
    try {
      const { data, error } = await supabase
        .from('resume_reviews')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setReviews(data || []);
    } catch (error) {
      console.error('Error loading reviews:', error);
      toast.error('Failed to load resume reviews');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async (review: ResumeReview) => {
    try {
      setIsDownloading(review.id);
      const response = await fetch(review.resume_url);
      const blob = await response.blob();
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `resume-${new Date(review.created_at).toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Resume downloaded successfully');
    } catch (error) {
      console.error('Error downloading resume:', error);
      toast.error('Failed to download resume');
    } finally {
      setIsDownloading(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedFile || !user) {
      toast.error('Please select a resume file and ensure you are signed in');
      return;
    }
    
    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    try {
      const timestamp = Date.now();
      const filePath = `reviews/${user.id}/${timestamp}/${selectedFile.name}`;
      
      // Upload file to storage
      const { data: storageData, error: storageError } = await supabase.storage
        .from('resumes')
        .upload(filePath, selectedFile);

      if (storageError) throw storageError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('resumes')
        .getPublicUrl(filePath);

      // Create review record
      const { error: reviewError } = await supabase
        .from('resume_reviews')
        .insert({
          user_id: user?.id,
          resume_url: publicUrl,
          email: email.toLowerCase(),
          status: 'pending'
        });

      if (reviewError) throw reviewError;
      
      toast.success('Resume submitted for review');
      setShowReviewContainer(true);
      setCurrentReview({
        id: crypto.randomUUID(),
        resume_url: publicUrl,
        status: 'pending',
        created_at: new Date().toISOString()
      });
      setEmail('');
      setSelectedFile(null);
      loadReviews();
    } catch (error) {
      console.error('Error submitting resume:', error); 
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('Failed to submit resume. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#ddd9d9] py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Back button */}
        <div className="mb-6">
          <Link
            to="/dashboard"
            className="inline-flex items-center text-[#800020] hover:opacity-80"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Dashboard
          </Link>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-lg mb-6">
          <h1 className="text-2xl font-bold text-black mb-4">Resume Review</h1>
          <p className="text-black mb-6">
            Get expert feedback on your resume from our career coaches. Upload your resume in PDF format and we'll provide detailed feedback within 48 hours.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="mb-4">
              <label htmlFor="email" className="block text-sm font-medium text-black mb-2">
                Email for Resume Review Updates
              </label>
              <div className="relative">
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-black placeholder-gray-400 focus:ring-2 focus:ring-accent-green focus:border-accent-green"
                  placeholder="Enter your email address"
                  required
                />
              </div>
              <p className="mt-1 text-sm text-gray-500">
                We'll send you updates about your resume review status
              </p>
            </div>

            <div id="upload-section" className="mb-8">
              <ResumeUpload
                userId={user?.id || ''}
                onUpload={(file) => setSelectedFile(file)}
                isSubmitting={isSubmitting}
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting || !selectedFile}
              className="w-full px-6 py-3 bg-[#800020] text-white rounded-xl hover:opacity-90 transition-colors disabled:opacity-50 flex items-center justify-center"
            >
              {isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Submitting...
                </>
              ) : (
                'Submit Resume for Review'
              )}
            </button>
          </form>
        </div>

        {/* Review Container */}
        {showReviewContainer && currentReview && (
          <div className="bg-white rounded-xl p-6 shadow-lg mb-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-4">
                <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm">
                  <Clock className="h-4 w-4 inline mr-1" />
                  Pending Review
                </span>
                <span className="text-sm text-gray-500">
                  Submitted {new Date(currentReview.created_at).toLocaleDateString()}
                </span>
              </div>
              <button
                onClick={() => handleDownload(currentReview)}
                className={`inline-flex items-center text-[#800020] hover:opacity-80 text-sm ${
                  isDownloading === currentReview.id ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <Download className={`h-4 w-4 mr-1 ${
                  isDownloading === currentReview.id ? 'animate-spin' : ''
                }`} />
                {isDownloading === currentReview.id ? 'Downloading...' : 'Download Resume'}
              </button>
            </div>

            {/* Comments Section */}
            <div className="mt-6">
              <h3 className="text-sm font-medium text-black mb-4">Comments</h3>
              <ResumeComments reviewId={currentReview.id} />
            </div>
          </div>
        )}

        {/* Previous Reviews */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : reviews.length > 0 ? (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-black">Previous Reviews</h2>
            {reviews.map((review) => (
              <div key={review.id} className="bg-white rounded-xl p-6 shadow-lg">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-2">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      review.status === 'pending'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {review.status === 'pending' ? (
                        <>
                          <Clock className="h-4 w-4 inline mr-1" />
                          Pending
                        </>
                      ) : (
                        <>
                          <CheckCircle className="h-4 w-4 inline mr-1" />
                          Reviewed
                        </>
                      )}
                    </span>
                    <span className="text-sm text-gray-500">
                      Submitted {new Date(review.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <button
                    onClick={() => handleDownload(review)}
                    className={`inline-flex items-center text-[#800020] hover:opacity-80 text-sm ${
                      isDownloading === review.id ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <Download className={`h-4 w-4 mr-1 ${
                      isDownloading === review.id ? 'animate-spin' : ''
                    }`} />
                    {isDownloading === review.id ? 'Downloading...' : 'Download Resume'}
                  </button>
                </div>
                
                {/* Admin Feedback */}
                {review.admin_feedback && (
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <h3 className="text-sm font-medium text-black mb-2">Feedback from Reviewer</h3>
                    <p className="text-black whitespace-pre-wrap">{review.admin_feedback}</p>
                  </div>
                )}
                
                {/* Comments Section */}
                <ResumeComments reviewId={review.id} />
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export { ResumeReviewPage };